import React, { useEffect, useRef, useState } from "react";
import { useSelector, useDispatch } from "react-redux";
import { Link } from "react-router-dom";
import { fetchMainCategories } from "../../store/slices/mainCategorySlice";
import { BiGridAlt } from "react-icons/bi";
import { FiChevronLeft, FiChevronRight } from "react-icons/fi";

const MainCategoryGrid: React.FC = () => {
  const dispatch = useDispatch();
  const { mainCategories, loading } = useSelector(
    (state: any) => state.mainCategory,
  );
  const scrollRef = useRef<HTMLDivElement>(null);

  const scrollBy = (dir: "left" | "right") => {
    if (scrollRef.current) {
      scrollRef.current.scrollBy({
        left: dir === "right" ? 320 : -320,
        behavior: "smooth",
      });
    }
  };

  useEffect(() => {
    if (!mainCategories || mainCategories.length === 0) {
      dispatch(fetchMainCategories() as any);
    }
  }, [dispatch, mainCategories]);

  const hasRealData = mainCategories && mainCategories.length > 0;
  const itemsToDisplay = [...(mainCategories || [])]
    .sort(
      (a: any, b: any) =>
        (Number(a.priority) || 999) - (Number(b.priority) || 999),
    )
    .map((cat: any) => ({
      _id: cat._id,
      name: cat.name,
      key: cat.key,
      description: cat.description || "",
      image: cat.image || "/images/categories/placeholder.png",
      priority: cat.priority,
    }));

  return (
    <div className="mcg-wrapper">
      <style>{`
                @import url('https://fonts.googleapis.com/css2?family=Outfit:wght@400;600;700;800&display=swap');

                .mcg-wrapper {
                    padding: 28px 20px 40px;
                    max-width: 1580px;
                    margin: 0 auto;
                    font-family: 'Outfit', sans-serif;
                    box-sizing: border-box;
                }

                /* ── Scroll Buttons ── */
                .mcg-scroll-btn {
                    display: flex;
                    align-items: center;
                    justify-content: center;
                    width: 36px;
                    height: 36px;
                    border-radius: 50%;
                    border: 1.5px solid #43c8ca;
                    background: #fff;
                    color: #43c8ca;
                    cursor: pointer;
                    transition: all 0.2s ease;
                    flex-shrink: 0;
                }
                .mcg-scroll-btn:hover {
                    background: #43c8ca;
                    color: #fff;
                }
                .mcg-scroll-btns {
                    display: flex;
                    gap: 8px;
                    align-items: center;
                }
                .mcg-header {
                    display: flex;
                    align-items: center;
                    justify-content: space-between;
                    margin-bottom: 20px;
                    
                }
                .mcg-title {
                    font-size: 22px;
                    font-weight: 800;
                    color: #1a1a1a;
                    margin: 0;
                    display: flex;
                    align-items: center;
                    gap: 8px;
                }
                .mcg-title-icon {
                    color: #43c8ca;
                    animation: mcg-pulse 1.5s ease-in-out infinite;
                }
                @keyframes mcg-pulse {
                    0%, 100% { transform: scale(1); }
                    50%       { transform: scale(1.25); }
                }

                /* ── Horizontal Scroll Row ── */
                .mcg-grid {
                    display: flex;
                    flex-direction: row;
                    gap: 16px;
                    overflow-x: auto;
                    overflow-y: visible;
                    padding-bottom: 8px;
                    scroll-snap-type: x mandatory;
                    -webkit-overflow-scrolling: touch;
                    scrollbar-width: none;
                }
                .mcg-grid::-webkit-scrollbar { 
                    height: 10px; 
                }
                .mcg-grid::-webkit-scrollbar-track {
                    background: #e0f2fe;
                    border-radius: 10px;
                }
                .mcg-grid::-webkit-scrollbar-thumb {
                    background: #38bdf8;
                    border-radius: 10px;
                    border: 2px solid #e0f2fe;
                }
                .mcg-grid::-webkit-scrollbar-thumb:hover {
                    background: #0284c7;
                }

                /* Each card shows 4 full + 5th peeking */
                .mcg-card-link {
                    flex: 0 0 calc(22.5% - 12px);
                    min-width: calc(22.5% - 12px);
                    scroll-snap-align: start;
                }
                @media (max-width: 1100px) {
                    .mcg-card-link { flex: 0 0 calc(30% - 11px); min-width: calc(30% - 11px); }
                }
                @media (max-width: 768px) {
                    .mcg-card-link { flex: 0 0 calc(45% - 8px); min-width: calc(45% - 8px); }
                    .mcg-wrapper { padding: 16px 12px 28px; }
                    .mcg-title { font-size: 17px; }
                }
                @media (max-width: 480px) {
                    .mcg-card-link { flex: 0 0 calc(68% - 8px); min-width: calc(68% - 8px); }
                }

                .mcg-card {
                    position: relative;
                    height: 300px;
                    border-radius: 16px;
                    overflow: hidden;
                    cursor: pointer;
                    width: 100%;
                    transition: transform 0.35s cubic-bezier(0.16,1,0.3,1), box-shadow 0.35s;
                    box-shadow: 0 4px 16px rgba(0,0,0,0.10);
                    background: #1e293b;
                }

                @media (max-width: 640px) {
                    .mcg-card { height: 220px; border-radius: 12px; }
                }

                /* Background image fills card */
                .mcg-card-bg {
                    position: absolute;
                    inset: 0;
                    background-size: cover;
                    background-position: center;
                }

                /* Gradient overlay */
                .mcg-card-overlay {
                    position: absolute;
                    inset: 0;
                    background: linear-gradient(
                        170deg,
                        rgba(0,0,0,0.05) 0%,
                        rgba(0,0,0,0.55) 70%,
                        rgba(0,0,0,0.82) 100%
                    );
                    z-index: 1;
                }

                /* Card content */
                .mcg-card-body {
                    position: absolute;
                    bottom: 0;
                    left: 0;
                    right: 0;
                    padding: 14px 14px 12px;
                    z-index: 2;
                }
                .mcg-card-name {
                    font-size: 14px;
                    font-weight: 400;
                    color: #fff;
                    margin: 0 0 5px 0;
                    line-height: 1.25;
                    text-shadow: 0 1px 4px rgba(0,0,0,0.5);
                    white-space: nowrap;
                    overflow: hidden;
                    text-overflow: ellipsis;
                }
                @media (max-width: 600px) {
                    .mcg-card-name { font-size: 12px; }
                }

                /* ── Skeleton ── */
                .mcg-skeleton-wrap {
                    flex: 0 0 calc(25% - 12px);
                    min-width: calc(25% - 12px);
                }
                .mcg-skeleton {
                    height: 300px;
                    border-radius: 16px;
                    background: #e8ecf0;
                    position: relative;
                    overflow: hidden;
                    width: 100%;
                }
                @media (max-width: 640px) {
                    .mcg-skeleton { height: 220px; border-radius: 12px; }
                }
                .mcg-shimmer {
                    position: absolute;
                    inset: 0;
                    background: linear-gradient(90deg, transparent 0%, rgba(255,255,255,0.55) 50%, transparent 100%);
                    animation: mcg-shimmer 1.4s infinite;
                }
                @keyframes mcg-shimmer {
                    0%   { transform: translateX(-100%); }
                    100% { transform: translateX(100%); }
                }
                .mcg-bottom-controls {
                    display: flex;
                    justify-content: center;
                    align-items: center;
                    margin-top: 20px;
                }
            `}</style>

      {/* Section Header */}
      <div className="mcg-header">
        <h2 className="mcg-title">
          <BiGridAlt className="mcg-title-icon" size={22} />
          Shop by Main Category
        </h2>
        <div className="mcg-scroll-btns">
          <button
            className="mcg-scroll-btn"
            onClick={() => scrollBy("left")}
            aria-label="Scroll left"
          >
            <FiChevronLeft size={18} />
          </button>
          <button
            className="mcg-scroll-btn"
            onClick={() => scrollBy("right")}
            aria-label="Scroll right"
          >
            <FiChevronRight size={18} />
          </button>
        </div>
      </div>

      <div className="mcg-grid" ref={scrollRef}>
        {loading && !hasRealData
          ? Array.from({ length: 8 }).map((_, i) => (
              <div key={i} className="mcg-skeleton-wrap">
                <div className="mcg-skeleton">
                  <div className="mcg-shimmer" />
                </div>
              </div>
            ))
          : itemsToDisplay?.map((item: any) => {
              const linkUrl = `/products?main_category=${item._id}`;
              return (
                <Link
                  to={linkUrl}
                  key={item._id}
                  className="mcg-card-link"
                  style={{
                    textDecoration: "none",
                    color: "inherit",
                    display: "block",
                  }}
                >
                  <div className="mcg-card">
                    <div
                      className="mcg-card-bg"
                      style={{ backgroundImage: `url(${encodeURI(item.image)})` }}
                    />
                    <div className="mcg-card-overlay" />
                    <div className="mcg-card-body">
                      <p className="mcg-card-name">{item.name}</p>
                    </div>
                  </div>
                </Link>
              );
            })}
      </div>

      {/* Bottom Scroll Buttons */}
      <div className="mcg-bottom-controls">
        <div className="mcg-scroll-btns">
          <button
            className="mcg-scroll-btn"
            onClick={() => scrollBy("left")}
            aria-label="Scroll left"
          >
            <FiChevronLeft size={18} />
          </button>
          <button
            className="mcg-scroll-btn"
            onClick={() => scrollBy("right")}
            aria-label="Scroll right"
          >
            <FiChevronRight size={18} />
          </button>
        </div>
      </div>
    </div>
  );
};

export default MainCategoryGrid;
